import {
  validLoadProfileQualities,
  validMeteringTypes,
} from './functions.constants';
import {
  AggregatedLoadProfileResponse,
  LoadProfileListResponse,
} from './types/loadprofile';
import { doRequest, setDateValues, setNumberFormat } from './util';
import { DateTime } from 'luxon';

/* global window, Excel, console, CustomFunctions */

export function parseIds(ids: string[][]) {
  if (typeof ids === 'string') {
    return [(ids as string).replace(/\s/g, '')];
  }
  let id_arr: any[] = [];
  for (let i = 0; i < ids.length; i++) {
    for (let j = 0; j < ids[i].length; j++) {
      ids[i][j].replace(/\s/g, '');
      let tmp_arr = ids[i][j].split(',');
      for (let k = 0; k < tmp_arr.length; k++) {
        if (tmp_arr[k]) {
          id_arr.push(tmp_arr[k]);
        }
      }
    }
  }
  return id_arr;
}

export async function getLoadProfileList(
  contract_portfolio_id?: string,
  managing_portfolio_id?: string,
  load_profile_name?: string,
  metering_point?: string,
  metering_type?: string,
  load_profile_quality?: string,
  delivery_type?: string,
  apiUrl: string = window.api_url
) {
  try {
    const validDeliveryTypes = ['consumption', 'production'];
    let url = new URL(apiUrl + '/loadprofiles/list');
    if (contract_portfolio_id) {
      url.searchParams.append('contract_portfolio_id', contract_portfolio_id);
    }
    if (managing_portfolio_id) {
      url.searchParams.append('portfolio_id', managing_portfolio_id);
    }
    if (load_profile_name) {
      url.searchParams.append('load_profile_name', load_profile_name);
    }
    if (metering_point) {
      url.searchParams.append('metering_point_number', metering_point);
    }
    if (metering_type) {
      if (validMeteringTypes.includes(metering_type)) {
        url.searchParams.append('metering_type', metering_type);
      } else {
        return [
          ['metering_type must be one of: ' + validMeteringTypes.join(';')],
        ];
      }
    }
    if (load_profile_quality) {
      if (
        validLoadProfileQualities.includes(load_profile_quality.toUpperCase())
      ) {
        url.searchParams.append(
          'quality_type',
          load_profile_quality.toUpperCase()
        );
      } else {
        return [
          [
            'load_profile_quality must be one of: ' +
              validLoadProfileQualities.join(';'),
          ],
        ];
      }
    }
    if (delivery_type) {
      if (validDeliveryTypes.includes(delivery_type)) {
        url.searchParams.append('delivery_type', delivery_type);
      } else {
        return [
          ['delivery_type must be one of: ' + validDeliveryTypes.join(';')],
        ];
      }
    }
    let response = await doRequest(url);
    if (!response.ok) {
      if (response.status === 500) {
        return [['unexpected error on server']];
      } else {
        let error = await response.json();
        return [
          [
            `failed request: ${response.status} ${response.statusText} ${error.message} ${url}`,
          ],
        ];
      }
    }
    let json: LoadProfileListResponse = await response.json();
    const headers = [
      'id',
      'name',
      'metering_point_id',
      'quality_type',
      'unit',
      'resolution',
      'time_zone',
      'description',
      'date_from',
      'date_to',
      'data_source',
      'sender',
      'receiver',
      'party',
      'status',
      'meta',
      'delivery_type',
    ];
    return [headers].concat(
      json.load_profiles.map((loadProfile) =>
        headers.map((header) => {
          if (header === 'meta' && loadProfile[header]) {
            return JSON.stringify(loadProfile[header]);
          }
          if (
            header === 'delivery_type' &&
            loadProfile.metering_point?.delivery_type
          ) {
            return loadProfile.metering_point.delivery_type;
          }
          return loadProfile[header] ?? '';
        })
      )
    );
  } catch (error: any) {
    console.error(error);
    return [['error occured in addin: ' + error.message]];
  }
}

export function parseIdsNew(ids: number[][]) {
  return ids.flat();
}

export async function getAggregatedLoadProfiles(
  load_profile_ids: string[][],
  contract_portfolio_id: string,
  managing_portfolio_ids: string[][],
  load_profile_quality: string,
  metering_point_type: string,
  resolution: string,
  unit: string,
  start_date: DateTime,
  end_date: DateTime,
  gap_warning: boolean,
  auto_update: boolean,
  exclude_gap_lps?: boolean,
  delivery_type?: string,
  consider_delivery_period?: boolean,
  start_of_day?: number,
  invocation?: CustomFunctions.Invocation,
  apiUrl: string = window.api_url
) {
  let id_arr = parseIds(load_profile_ids);
  const managing_portfolio_id_arr: string[] = parseIds(managing_portfolio_ids);
  let url = new URL(apiUrl + '/loadprofiles/aggregate');
  if (
    id_arr &&
    id_arr.length > 0 &&
    (contract_portfolio_id ||
      (managing_portfolio_id_arr && managing_portfolio_id_arr.length > 0))
  ) {
    return [
      [
        'Error: you should either provide load_profile_ids or a contract_portfolio_id, but not both of them',
      ],
    ];
  } else if (id_arr && id_arr.length > 0) {
    id_arr.forEach((id) => url.searchParams.append('ids', id));
  } else {
    if (contract_portfolio_id) {
      url.searchParams.append('contract_portfolio_id', contract_portfolio_id);
    }
    if (managing_portfolio_id_arr) {
      managing_portfolio_id_arr.forEach((id) =>
        url.searchParams.append('managing_portfolio_ids', id)
      );
    }
  }

  let params = {
    load_profile_quality,
    metering_point_type,
    resolution,
    unit,
    start_date: start_date.toFormat('yyyy-MM-dd'),
    end_date: end_date.toFormat('yyyy-MM-dd'),
    gap_warning,
    exclude_gap_lps,
    delivery_type,
    consider_delivery_period,
    start_of_day,
  };
  Object.keys(params).forEach((key) => {
    if (
      params[key] ||
      (typeof params[key] === 'boolean' && params[key] !== undefined) ||
      (key === 'start_of_day' &&
        params[key] !== undefined &&
        params[key] !== null)
    ) {
      url.searchParams.append(key, params[key]);
    }
  });

  let response = await doRequest(url);
  if (!response.ok) {
    let error = await response.json();
    return [
      [
        `failed request: ${response.status} ${response.statusText} ${error.message} ${url}`,
      ],
    ];
  }

  try {
    let json: AggregatedLoadProfileResponse = await response.json();
    let load_profile_aggregates = json.time_series[0].values;
    let details = json.time_series[0].details;
    let address = invocation!.address;
    let headers: string[][] = [[], []];
    unit = unit || details.unit;
    resolution = resolution || details.resolution;

    let numberFormat =
      resolution.includes('hour') || resolution.includes('min')
        ? 'dd.mm.yyyy hh:MM'
        : resolution.includes('month')
          ? 'mm.yyyy'
          : 'dd.mm.yyyy';
    if (auto_update) {
      load_profile_aggregates = setDateValues(load_profile_aggregates);
    } else {
      load_profile_aggregates.forEach((load_profile, i) => {
        load_profile[0] = `=VALUE("${
          (load_profile[0] + '').replace('T', ' ').split('+')[0]
        }")`;
      });
    }

    load_profile_aggregates.forEach((load_profile: any[]) => {
      load_profile.splice(1, load_profile.length - 2);
    });

    await setNumberFormat(
      numberFormat,
      load_profile_aggregates.length - 1,
      0,
      2,
      0,
      address
    );
    headers[0].push(
      '',
      `ids_without_gaps: {${details.ids_without_gaps.join(',')}}`
    );
    headers[1].push(
      unit,
      `ids_with_gaps: {${details.ids_with_gaps.join(',')}}`
    );
    load_profile_aggregates.unshift(headers[1], headers[0]);

    if (!auto_update) {
      let context = new Excel.RequestContext();
      let range = context.workbook.worksheets
        .getActiveWorksheet()
        .getRange(address)
        .getResizedRange(
          load_profile_aggregates.length - 1,
          load_profile_aggregates[0].length - 1
        );
      range.values = load_profile_aggregates;
      return await context.sync();
    } else {
      return load_profile_aggregates;
    }
  } catch (error: any) {
    console.error(error);
    return [['error occured in addin: ' + error.message]];
  }
}

export async function getLoadProfilesByIds(
  load_profile_ids: string[],
  resolution: string,
  unit: string,
  start_date: DateTime,
  end_date: DateTime,
  enforce_length: boolean = true,
  auto_update: boolean,
  start_of_day: number | null,
  address: string,
  apiUrl: string = window.api_url
) {
  let url = apiUrl + '/loadprofiles/data?';
  const validResolutions = ['15 min', '1 hour', '1 day', '1 month'];
  const validUnits = ['kW', 'MW', 'GW', 'kWh', 'MWh', 'GWh'];
  for (let i = 0; i < load_profile_ids.length; i++) {
    if (i === 0) {
      url += 'ids=' + load_profile_ids[i];
    } else {
      url += '&ids=' + load_profile_ids[i];
    }
  }
  if (resolution !== undefined && resolution !== '') {
    if (validResolutions.includes(resolution)) {
      url += '&resolution=' + encodeURIComponent(resolution);
    } else {
      return [
        ['if resolution is provided, it must be one of: [15 min;1 hour;1 day]'],
      ];
    }
  }
  if (unit !== undefined && unit !== '') {
    if (validUnits.includes(unit)) {
      url += '&unit=' + encodeURIComponent(unit);
    } else {
      return [
        ['if unit is provided, it must be one of: [kW;MW;GW;kWh;MWh;GWh]'],
      ];
    }
  }
  if (start_date) {
    url +=
      '&start_date=' + encodeURIComponent(start_date.toFormat('yyyy-MM-dd'));
  }
  if (end_date) {
    url += '&end_date=' + encodeURIComponent(end_date.toFormat('yyyy-MM-dd'));
  }
  if (start_of_day !== undefined && start_of_day !== null) {
    url += '&start_of_day=' + encodeURIComponent(start_of_day);
  }
  if (!enforce_length && load_profile_ids.length > 1) {
    return [['if multiple ids are requested enforce_length needs to be true']];
  } else if (!enforce_length) {
    url += '&enforce_length=' + enforce_length;
  }
  let response = await doRequest(url);
  if (!response.ok) {
    let error = await response.json();
    return [
      [
        'failed request: ' +
          response.status +
          ' ' +
          response.statusText +
          ' ' +
          error.message +
          ' ' +
          url,
      ],
    ];
  }
  try {
    let json = await response.json();
    let time_series_count = json.time_series.length;
    let load_profile_values = json.time_series[0].values;
    let details = json.time_series[0].details;
    let headers: any[][] = [[], []];
    if (unit === null || unit === undefined || unit === '') {
      unit = details.unit;
    }
    if (resolution === null || resolution === undefined || resolution === '') {
      resolution = details.resolution;
    }
    let numberFormat = 'dd.mm.yyyy';
    if (resolution.includes('hour') || resolution.includes('min')) {
      numberFormat = 'dd.mm.yyyy hh:MM';
    }
    if (resolution.includes('month')) {
      numberFormat = 'mm.yyyy';
    }
    if (auto_update) {
      load_profile_values = setDateValues(load_profile_values);
    } else {
      for (let i = 0; i < load_profile_values.length; i++) {
        load_profile_values[i][0] = `=VALUE("${
          load_profile_values[i][0].replace('T', ' ').split('+')[0]
        }")`;
      }
    }
    load_profile_values.forEach((load_profile: any[]) => {
      load_profile.splice(1, load_profile.length - 2);
    });
    await setNumberFormat(
      numberFormat,
      load_profile_values.length - 1,
      0,
      2,
      0,
      address
    );
    headers[0].push(details.id);
    headers[1].push(details.name);
    const nv_value = 0; // TODO change to new CustomFunctions thingy
    for (let i = 1; i < time_series_count; i++) {
      let values = json.time_series[i].values;
      details = json.time_series[i].details;
      let values_length = values.length;
      for (let j = 0; j < values_length; j++) {
        values[j].splice(0, values[j].length - 1);
        load_profile_values[j][i + 1] =
          values[j][0] === null || values[j][0] === undefined
            ? auto_update
              ? nv_value
              : '=na()'
            : values[j][0];
      }
      headers[0].push(details.id);
      headers[1].push(details.name);
    }
    headers[0].unshift('');
    headers[1].unshift(unit);
    load_profile_values.unshift(headers[1]);
    load_profile_values.unshift(headers[0]);
    if (auto_update) {
      return load_profile_values;
    } else {
      let context = new Excel.RequestContext();
      let range = context.workbook.worksheets
        .getActiveWorksheet()
        .getRange(address)
        .getResizedRange(
          load_profile_values.length - 1,
          load_profile_ids.length
        );
      range.values = load_profile_values;
      await context.sync();
    }
  } catch (error: any) {
    console.error(error);
    return [['error occurred in addin: ' + error.message]];
  }
}
